/**
 * The Appointments tab's client-side filter chain, as one pure function so the tab's list and
 * the CSV export's "all results" apply exactly the same rules. The type is filtered server-side;
 * this covers the status chips and the search box:
 *   - status in `selectedStatuses` (when any are selected)
 *   - `search` matches as a case-insensitive substring across the columns the table shows
 */

export interface FilterableAppointment {
  id: number;
  status?: string | null;
  consumer_name?: string | null;
  consumer_phone?: string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  vehicle_year?: number | string | null;
  vehicle_vin?: string | null;
}

export interface AppointmentFilters {
  search: string;
  selectedStatuses: string[];
}

/** The status chips as a server-side condition, for the queries that can take one. Match-all when none are selected. */
export const toStatusFilter = (selectedStatuses: readonly string[]): { status?: { _in: string[] } } =>
  selectedStatuses.length === 0 ? {} : { status: { _in: [...selectedStatuses] } };

export const filterMobileServiceAppointments = <T extends FilterableAppointment>(
  all: readonly T[],
  { search, selectedStatuses }: AppointmentFilters
): T[] => {
  const byStatus = selectedStatuses.length === 0 ? [...all] : all.filter(a => selectedStatuses.includes(a.status ?? ''));

  const q = search.trim().toLowerCase();
  if (!q) return byStatus;
  return byStatus.filter(a => {
    return (
      (a.id != null && String(a.id).includes(q)) ||
      (a.consumer_name && a.consumer_name.toLowerCase().includes(q)) ||
      (a.consumer_phone && a.consumer_phone.toLowerCase().includes(q)) ||
      (a.vehicle_make && a.vehicle_make.toLowerCase().includes(q)) ||
      (a.vehicle_model && a.vehicle_model.toLowerCase().includes(q)) ||
      (a.vehicle_year && String(a.vehicle_year).includes(q)) ||
      (a.vehicle_vin && a.vehicle_vin.toLowerCase().includes(q)) ||
      (a.status && a.status.toLowerCase().includes(q))
    );
  });
};
